<template>
	<div>
		<div class="row content-header">
			<div class="content-header-left mb-2 col-md-9 col-12">
				<div class="row breadcrumbs-top">
					<div class="col-12 d-flex align-items-center">
						<feather-icon icon="ArrowLeftIcon" size="24" class="mr-2" @click="goBack" />
						<h2 class="content-header-title mb-0">Add Formula</h2>
					</div>
				</div>
			</div>
		</div>

		<validation-observer ref="observer">
			<b-form @submit="submitFormula" method="post">
				<b-card>
					<b-row>
						<!-- Feed -->
						<b-col md="6">
							<b-form-group>
								<template v-slot:label>Feed <span class="text-danger">*</span></template>
								<validation-provider #default="{ errors }" name="Feed" rules="required">
									<v-select
										v-model="form.feed_id"
										:options="feedOptions"
										label="name"
										:reduce="(f) => f.id"
										:clearable="false"
									/>
									<small class="text-danger">{{ errors[0] }}</small>
								</validation-provider>
							</b-form-group>
						</b-col>

						<!-- Version -->
						<b-col md="3">
							<b-form-group>
								<template v-slot:label>Version <span class="text-danger">*</span></template>
								<validation-provider
									#default="{ errors }"
									name="Version"
									:rules="{ required: true, regex: versionPattern }"
								>
									<b-form-input v-model="form.version" placeholder="e.g. 1" type="text" maxlength="6" />
									<small class="text-danger">{{ errors[0] }}</small>
								</validation-provider>
							</b-form-group>
						</b-col>

						<!-- Active -->
						<b-col md="3" class="d-flex align-items-center">
							<b-form-group label="Active" class="mb-0 w-100">
								<b-form-checkbox switch v-model="form.is_active" name="is_active">
									{{ form.is_active ? "Yes" : "No" }}
								</b-form-checkbox>
							</b-form-group>
						</b-col>
					</b-row>

					<!-- Items header -->
					<div class="d-flex align-items-center mt-2 mb-1">
						<h5 class="mb-0">Items</h5>
						<b-button size="sm" variant="primary" class="ml-2" @click="addItem">+ Add Row</b-button>
						<b-badge class="ml-auto" variant="light-primary">Total: {{ totalPercentage.toFixed(2) }}%</b-badge>
					</div>

					<b-alert v-if="!isTotalValid" show variant="warning" class="py-1 px-2 mb-2">
						Percentages must sum to <strong>100%</strong>.
					</b-alert>

					<!-- Items table -->
					<div class="table-responsive overflow-visible">
						<table class="table table-sm">
							<thead>
								<tr>
									<th style="width: 48%">Raw Material</th>
									<th style="width: 32%">Percentage (%)</th>
									<th style="width: 20%" class="text-right">Action</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(row, idx) in form.items" :key="row.uid">
									<td>
										<validation-provider :name="'Raw Material ' + (idx + 1)" rules="required" v-slot="{ errors }">
											<v-select
												v-model="row.raw_material_id"
												:options="filteredRawMaterialOptions(idx)"
												label="name"
												:reduce="(r) => r.id"
												:clearable="false"
												track-by="id"
											>
												<template #option="{ name, unit }">
													<div class="d-flex justify-content-between w-100">
														<span>{{ name }}</span>
														<small v-if="unit && unit.name" class="text-muted">({{ unit.name }})</small>
													</div>
												</template>
												<template #selected-option="{ name }">
													<span>{{ name }}</span>
												</template>
											</v-select>
											<small class="text-danger">{{ errors[0] }}</small>
										</validation-provider>
									</td>

									<td>
										<validation-provider
											:name="'Percentage ' + (idx + 1)"
											:rules="{ required: true, regex: percentagePattern }"
											v-slot="{ errors }"
										>
											<b-input-group>
												<b-form-input
													v-model="row.percentage"
													type="number"
													step="0.01"
													min="0"
													max="100"
													inputmode="decimal"
													placeholder="0.00"
													@blur="normalizeRowPercentage(row)"
												/>
												<b-input-group-append>
													<b-input-group-text>%</b-input-group-text>
												</b-input-group-append>
											</b-input-group>
											<small class="text-danger">{{ errors[0] }}</small>
										</validation-provider>
									</td>

									<td class="text-right">
										<b-button size="sm" variant="danger" @click="removeItem(idx)" :disabled="form.items.length === 1">
											Remove
										</b-button>
									</td>
								</tr>
							</tbody>
						</table>
					</div>

					<!-- Submit -->
					<div class="mt-2">
						<b-button
							type="submit"
							variant="primary"
							class="mr-1"
							:disabled="submitting || !isTotalValid || form.items.length === 0 || hasDuplicateSelection"
						>
							{{ submitting ? "Submitting..." : "Submit" }}
						</b-button>
						<b-button type="button" variant="outline-secondary" @click="goBack">Cancel</b-button>
					</div>
				</b-card>
			</b-form>
		</validation-observer>
	</div>
</template>

<script>
import vSelect from "vue-select";
import axiosIns from "@/libs/axios";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";

export default {
	components: { vSelect, ValidationProvider, ValidationObserver },
	data() {
		return {
			userData: null,
			submitting: false,

			// dropdown data
			feedOptions: [],
			rawMaterialOptions: [],

			// validation patterns
			versionPattern: /^\d+$/, // 1, 2, 10 ...
			percentagePattern: /^\d+(\.\d{1,2})?$/, // 0, 12, 12.5, 12.34

			// main form
			form: {
				feed_id: null,
				is_active: true,
				version: "1",
				items: [{ uid: Date.now(), raw_material_id: null, percentage: "" }],
			},
		};
	},
	computed: {
		totalPercentage() {
			return this.form.items.reduce((sum, r) => {
				const n = parseFloat((r.percentage || "").toString());
				return sum + (isNaN(n) ? 0 : n);
			}, 0);
		},
		isTotalValid() {
			return Math.abs(this.totalPercentage - 100) < 0.001; // tolerate float drift
		},
		// Always compare IDs as strings to avoid 1 vs "1" mismatches
		selectedRawMaterialIds() {
			return this.form.items
				.map((r) => (r.raw_material_id == null ? null : String(r.raw_material_id)))
				.filter((id) => id !== null);
		},
		hasDuplicateSelection() {
			const seen = new Set();
			for (let i = 0; i < this.selectedRawMaterialIds.length; i++) {
				const id = this.selectedRawMaterialIds[i];
				if (seen.has(id)) return true;
				seen.add(id);
			}
			return false;
		},
	},
	created() {
		this.userData = JSON.parse(sessionStorage.getItem("userData") || localStorage.getItem("userData") || "null");
		this.fetchFeeds();
		this.fetchRawMaterials();
	},
	methods: {
		goBack() {
			this.$router.go(-1);
		},

		// ----- Fetch feeds (your API shows feed_list) -----
		fetchFeeds() {
			axiosIns
				.get(`web/feeds`)
				.then((res) => {
					const feeds =
						res && res.data && res.data.feed_list && Array.isArray(res.data.feed_list.data)
							? res.data.feed_list.data
							: [];
					// minimal normalization (ensure id & name)
					this.feedOptions = feeds.map((f) => ({
						id: f && f.id != null ? f.id : null,
						name: f && f.name != null ? f.name : `Feed #${f && f.id != null ? f.id : ""}`,
					}));
				})
				.catch((err) => {
					console.error(err && err.response ? err.response.data : err);
				});
		},

		// ----- Fetch raw materials (your API shows raw_materials -> data[]) -----
		fetchRawMaterials() {
			const companyId = this.userData && this.userData.company ? this.userData.company.id : null;
			axiosIns
				.get(`web/company/${companyId}/raw-materials`)
				.then((res) => {
					const list =
						res && res.data && res.data.raw_materials && Array.isArray(res.data.raw_materials.data)
							? res.data.raw_materials.data
							: [];

					// Normalize to a clean option shape
					this.rawMaterialOptions = list.map((rm) => ({
						id: rm && rm.id != null ? rm.id : null,
						name: rm && rm.name != null ? rm.name : `RM #${rm && rm.id != null ? rm.id : ""}`,
						unit: rm && rm.unit ? { id: rm.unit.id, name: rm.unit.name } : null,
					}));
				})
				.catch((err) => {
					console.error(err && err.response ? err.response.data : err);
				});
		},

		// Hide already-selected materials (except the current row's own selection)
		filteredRawMaterialOptions(idx) {
			const currentId =
				this.form.items[idx] && this.form.items[idx].raw_material_id != null
					? String(this.form.items[idx].raw_material_id)
					: null;

			// Everything selected in *other* rows:
			const selectedSet = new Set(this.selectedRawMaterialIds.filter((id) => id !== currentId));

			// Keep options whose string id is not in the set
			return this.rawMaterialOptions.filter((opt) => {
				const oid = opt && opt.id != null ? String(opt.id) : "__null__";
				return !selectedSet.has(oid);
			});
		},

		addItem() {
			this.form.items.push({
				uid: Date.now() + Math.random(),
				raw_material_id: null,
				percentage: "",
			});
		},

		removeItem(idx) {
			if (this.form.items.length > 1) this.form.items.splice(idx, 1);
		},

		normalizeRowPercentage(row) {
			const val = parseFloat((row.percentage || "").toString());
			if (isNaN(val)) {
				row.percentage = "";
				return;
			}
			const clamped = Math.max(0, Math.min(100, val));
			row.percentage = clamped.toFixed(2);
		},

		submitFormula(e) {
			e.preventDefault();

			this.$refs.observer.validate().then((ok) => {
				if (!ok) return;

				if (this.hasDuplicateSelection) {
					this.$bvToast.toast("Each raw material must be unique.", {
						title: "Validation",
						variant: "warning",
						solid: true,
					});
					return;
				}

				if (!this.isTotalValid) {
					this.$bvToast.toast("Percentages must sum to 100%.", {
						title: "Validation",
						variant: "warning",
						solid: true,
					});
					return;
				}

				this.submitting = true;

				const companyId = this.userData && this.userData.company ? this.userData.company.id : null;
				const url = `web/company/${companyId}/feed-formulas`;

				const payload = {
					feed_id: this.form.feed_id,
					is_active: !!this.form.is_active,
					version: Number(this.form.version),
					items: this.form.items.map((r) => ({
						raw_material_id: r.raw_material_id,
						percentage: Number(r.percentage),
					})),
				};

				axiosIns
					.post(url, payload)
					.then(() => {
						this.$toast({
							component: ToastificationContent,
							position: "top-right",
							props: {
								icon: "CoffeeIcon",
								variant: "success",
								text: "Formula added successfully",
							},
						});
						this.goBack();
					})
					.catch((error) => {
						const errs =
							error && error.response && error.response?.data && error.response?.data?.errors
								? error.response?.data?.errors
								: null;
						const msg = errs ? Object.values(errs).flat().join(", ") : "Failed to add formula";
						this.$toast({
							component: ToastificationContent,
							position: "top-right",
							props: { icon: "CoffeeIcon", variant: "danger", text: msg },
						});
					})
					.finally(() => {
						this.submitting = false;
					});
			});
		},
	},
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
<style>
.overflow-visible {
	overflow: visible !important;
}
</style>